import React, { useState, useEffect, useMemo } from 'react';
import { Sparkles, X, CheckSquare, Square, CalendarPlus, Calendar as CalendarIcon, Loader2 } from 'lucide-react';

/**
 * VoiceInsightsCard — shows AI-extracted summary + action items from a voice note.
 * Each action item may carry a `due_date` (YYYY-MM-DD) and `due_time` (HH:MM).
 * When present, a "Schedule" button appears that converts the item into a draft
 * calendar appointment via props.onSchedule.
 *
 * Props:
 *   - transcript: string
 *   - summary: string
 *   - actionItems: Array<string | { task, due_date, due_time }>
 *   - onInsert: (text: string) => void
 *   - onSchedule: (item: { task, due_date, due_time }, idx: number) => Promise<void> | void
 *   - scheduledItems: Set<number>  // indices that have already been scheduled
 *   - schedulingIdx: number | null  // index currently posting
 *   - onDismiss: () => void
 */
export function VoiceInsightsCard({
  transcript,
  summary,
  actionItems,
  onInsert,
  onSchedule,
  scheduledItems,
  schedulingIdx,
  onDismiss,
}) {
  // Normalize action items — support legacy string format too.
  const normalizedItems = useMemo(
    () =>
      (actionItems || []).map((item) =>
        typeof item === 'string'
          ? { task: item, due_date: null, due_time: null }
          : { task: item.task || '', due_date: item.due_date || null, due_time: item.due_time || null },
      ),
    [actionItems],
  );

  const [checkedItems, setCheckedItems] = useState(() => new Set(normalizedItems.map((_, i) => i)));
  const [useSummary, setUseSummary] = useState(!!summary);

  useEffect(() => {
    setCheckedItems(new Set(normalizedItems.map((_, i) => i)));
    setUseSummary(!!summary);
  }, [summary, normalizedItems]);

  if (!transcript && !summary && normalizedItems.length === 0) return null;

  const toggleItem = (idx) => {
    const next = new Set(checkedItems);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setCheckedItems(next);
  };

  const buildNoteText = () => {
    const parts = [];
    if (useSummary && summary) {
      parts.push(summary);
    } else if (!summary) {
      parts.push(transcript);
    }
    const selected = normalizedItems
      .map((item, i) => ({ item, i }))
      .filter(({ i }) => checkedItems.has(i));
    if (selected.length > 0) {
      parts.push('');
      parts.push('Action items:');
      selected.forEach(({ item }) => {
        const datePart = item.due_date
          ? ` (due ${formatDateShort(item.due_date)}${item.due_time ? ` ${item.due_time}` : ''})`
          : '';
        parts.push(`  • ${item.task}${datePart}`);
      });
    }
    return parts.join('\n').trim();
  };

  return (
    <div
      className="mb-3 border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 rounded-sm p-3 sm:p-4"
      data-testid="voice-insights-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-violet-500" />
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-violet-700">AI Insights</span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-700 p-0.5"
          aria-label="Dismiss insights"
          data-testid="voice-insights-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {summary ? (
        <button
          type="button"
          onClick={() => setUseSummary(!useSummary)}
          className="w-full text-left mb-3 flex items-start gap-2 group"
          data-testid="toggle-summary"
        >
          {useSummary ? <CheckSquare className="w-3.5 h-3.5 text-[#0047AB] shrink-0 mt-0.5" /> : <Square className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 mb-0.5">Summary</p>
            <p className="text-sm text-slate-700 leading-snug">{summary}</p>
          </div>
        </button>
      ) : (
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 mb-0.5">Transcript</p>
          <p className="text-sm text-slate-700 leading-snug">{transcript}</p>
        </div>
      )}

      {normalizedItems.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Action Items</p>
          <div className="space-y-1.5">
            {normalizedItems.map((item, i) => {
              const checked = checkedItems.has(i);
              const alreadyScheduled = scheduledItems?.has(i);
              const posting = schedulingIdx === i;
              return (
                <div
                  key={`action-${i}`}
                  className="flex items-start gap-2 py-0.5"
                  data-testid={`action-item-row-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(i)}
                    className="flex items-start gap-2 text-left flex-1 min-w-0"
                    data-testid={`action-item-${i}`}
                  >
                    {checked
                      ? <CheckSquare className="w-3.5 h-3.5 text-[#0047AB] shrink-0 mt-0.5" />
                      : <Square className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-0.5" />}
                    <div className="min-w-0 flex-1">
                      <span className={`text-sm leading-snug ${checked ? 'text-slate-800' : 'text-slate-500'}`}>
                        {item.task}
                      </span>
                      {item.due_date && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5 align-middle"
                          data-testid={`action-item-due-${i}`}
                        >
                          <CalendarIcon className="w-3 h-3" />
                          {formatDateShort(item.due_date)}{item.due_time ? ` · ${item.due_time}` : ''}
                        </span>
                      )}
                    </div>
                  </button>
                  {onSchedule && (
                    <button
                      type="button"
                      onClick={() => onSchedule(item, i)}
                      disabled={posting || alreadyScheduled}
                      className={`text-[11px] font-semibold rounded-sm px-2 py-1 shrink-0 flex items-center gap-1 border transition-colors ${
                        alreadyScheduled
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default'
                          : 'bg-white border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-60'
                      }`}
                      data-testid={`schedule-action-item-${i}`}
                      title={item.due_date ? `Schedule for ${formatDateShort(item.due_date)}` : 'Schedule appointment'}
                    >
                      {posting
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <CalendarPlus className="w-3 h-3" />}
                      {alreadyScheduled ? 'Scheduled' : 'Schedule'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-violet-200/50">
        <button
          type="button"
          onClick={() => onInsert(buildNoteText())}
          className="bg-[#0047AB] hover:bg-[#003580] text-white text-xs font-semibold rounded-sm px-3 py-1.5"
          data-testid="insert-insights-btn"
        >
          Insert into note
        </button>
        {summary && (
          <button
            type="button"
            onClick={() => onInsert(transcript)}
            className="text-[11px] text-slate-500 hover:text-slate-800"
            data-testid="insert-raw-transcript"
          >
            Use raw transcript
          </button>
        )}
      </div>
    </div>
  );
}

function formatDateShort(isoDate) {
  try {
    const [y, m, d] = isoDate.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return isoDate;
  }
}
